import { OMSUserSchema } from "@redotech/redo-fulfillment-group-common/oms-user";
import * as zExt from "@redotech/rpc/ext";
import { privacy } from "@redotech/schema-privacy";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export enum FulfillmentBatchStatus {
  PENDING_LABEL_PURCHASE = "pending",
  LABELS_CREATED = "labels_created",
  LABELS_PRINTED = "labels_printed",
  PENDING_MANUAL_FULFILLMENT = "pending_manual_fulfillment",
  MANUALLY_FULFILLED = "manually_fulfilled",
  FAILED = "failed",
  PICKED_UP = "picked_up",
  READY_FOR_PICKUP = "ready_for_pickup",
  PARTIALLY_PICKED_UP = "partially_picked_up",
}

export enum BatchHistoryTableColumnKeys {
  SELECTED = "selected",
  BATCH_ID = "batchId",
  BATCH_DATE = "batchDate",
  ORDERS = "orders",
  SHIPMENTS = "shipments",
  BATCH_STATUS = "batchStatus",
  ERRORS = "errors",
  CREATED_BY = "createdBy",
  NUMBER_OF_FULFILLMENTS = "numberOfFulfillments",
}

const FulfillmentBatchErrorSchema_ = z.object({
  fulfillmentGroupId: zExt.objectId().meta({ privacy: privacy.id.internal }),
  message: z.string().meta({ privacy: privacy.systemErrorText }),
});
export const FulfillmentBatchErrorSchema: z.ZodType<FulfillmentBatchError> =
  FulfillmentBatchErrorSchema_;

export interface FulfillmentBatchError extends z.infer<
  typeof FulfillmentBatchErrorSchema_
> {}

const FulfillmentBatchDocuments_ = z.object({
  shippingLabel: z.string().meta({ privacy: privacy.privateUrl }).nullish(),
  packingSlip: z.string().meta({ privacy: privacy.privateUrl }).nullish(),
  shippingLabel_packingSlip: z
    .string()
    .meta({ privacy: privacy.privateUrl })
    .nullish(),
  shippingLabel_returnLabel: z
    .string()
    .meta({ privacy: privacy.privateUrl })
    .nullish(),
  shippingLabel_returnLabel_packingSlip: z
    .string()
    .meta({ privacy: privacy.privateUrl })
    .nullish(),
  returnLabel: z.string().meta({ privacy: privacy.privateUrl }).nullish(),
  pickList: z.string().meta({ privacy: privacy.privateUrl }).nullish(),
  commercialInvoice: z.string().meta({ privacy: privacy.privateUrl }).nullish(),
  personalizedSlip: z.string().meta({ privacy: privacy.privateUrl }).nullish(),
});
export const FulfillmentBatchDocuments: z.ZodType<FulfillmentBatchDocuments> =
  FulfillmentBatchDocuments_;
export interface FulfillmentBatchDocuments extends z.infer<
  typeof FulfillmentBatchDocuments_
> {}

const FulfillmentBatchSchema_ = z
  .object({
    _id: zExt.objectId().meta({ privacy: privacy.id.internal }),
    id: z.string().meta({ privacy: privacy.id.internal }),
    team: zExt.objectId().meta({ privacy: privacy.id.internal }),
    batchDate: z.date().meta({ privacy: privacy.timestamp }),
    orderNumbers: z
      .array(z.string().meta({ privacy: privacy.id.external.resource }))
      .meta({ privacy: privacy.structured }),
    fulfillmentGroupIds: z
      .array(zExt.objectId().meta({ privacy: privacy.id.internal }))
      .meta({ privacy: privacy.structured }),
    failedFulfillmentGroupIds: z
      .array(zExt.objectId().meta({ privacy: privacy.id.internal }))
      .nullish()
      .meta({ privacy: privacy.structured }),
    syncedOrderIds: z
      .array(z.string().meta({ privacy: privacy.id.external.resource }))
      .nullish()
      .meta({ privacy: privacy.structured }),
    errors: z
      .array(FulfillmentBatchErrorSchema_)
      .nullish()
      .meta({ privacy: privacy.structured }),
    boughtRates: z
      .record(
        z.string().meta({ privacy: privacy.id.internal }),
        zExt.objectId().meta({ privacy: privacy.id.internal }),
      )
      .nullish()
      .meta({ privacy: privacy.untypedPayload }),
    boughtReturnRates: z
      .record(
        z.string().meta({ privacy: privacy.id.internal }),
        zExt.objectId().meta({ privacy: privacy.id.internal }),
      )
      .nullish()
      .meta({ privacy: privacy.untypedPayload }),
    shipmentIds: z
      .array(z.string().meta({ privacy: privacy.id.external.resource }))
      .meta({ privacy: privacy.structured }),
    batchStatus: createEnum(FulfillmentBatchStatus, {
      enumName: "FulfillmentBatchStatus",
    }).meta({ privacy: privacy.enumOrStatus }),
    documents: FulfillmentBatchDocuments_.nullish().meta({
      privacy: privacy.structured,
    }),
    createdBy: OMSUserSchema.nullish().meta({ privacy: privacy.structured }),
    containsCommercialInvoice: z
      .boolean()
      .meta({ privacy: privacy.flag })
      .nullish(),
    /** @deprecated Use each fulfillment group's settings. */
    notifyMarketplace: z.boolean().meta({ privacy: privacy.flag }).nullish(),
    /** @deprecated Use each fulfillment group's settings. */
    fulfillmentDate: z.date().meta({ privacy: privacy.timestamp }).nullish(),
    insured: z.boolean().meta({ privacy: privacy.flag }).nullish(),
    updatedAt: z.date().meta({ privacy: privacy.timestamp }).nullish(),
    local: z.boolean().meta({ privacy: privacy.flag }).nullish(),
    packingSlipIds: z
      .array(z.string().meta({ privacy: privacy.id.internal }))
      .nullish()
      .meta({ privacy: privacy.structured }),
    packingSlipPrintedAt: z
      .date()
      .meta({ privacy: privacy.timestamp })
      .nullish(),
    orderSummaryPrintedAt: z
      .date()
      .meta({ privacy: privacy.timestamp })
      .nullish(),
    voidableFulfillmentGroupIds: z
      .array(zExt.objectId().meta({ privacy: privacy.id.internal }))
      .nullish()
      .meta({ privacy: privacy.structured }),
  })
  .meta({ privacy: privacy.structured });
export const FulfillmentBatchSchema: z.ZodType<FulfillmentBatch> =
  FulfillmentBatchSchema_;
export interface FulfillmentBatch extends z.infer<
  typeof FulfillmentBatchSchema_
> {}
export type FulfillmentBatchDocumentKind = keyof z.infer<
  typeof FulfillmentBatchDocuments_
>;
